<template>
  <div>
    <b-row>
      <!-- Affichage de l'audio actuel ou nouvellement selectionné -->
      <b-col cols="12" :md="buttonsPosition == 'right' ? 8 : 12">
        <div
          class="audio-container"
          :class="{
            empty: !hasSelectedAudio,
            'cursor-pointer': emptyContainerAction !== 'none',
          }"
          @click="handleEmptyContainerClick">
          <b-overlay :show="uploading">
            <div v-if="hasSelectedAudio" class="audio-player">
              <audio controls :src="selectedAudio.path" preload="none"></audio>
              <p>{{ selectedAudio.name }}</p>
            </div>

            <div v-else class="placeholder">
              <span>Aucun audio sélectionné</span>
            </div>
          </b-overlay>
        </div>
      </b-col>

      <!-- Boutons d'actions -->
      <b-col cols="12" :md="buttonsPosition == 'right' ? 4 : 12" class="mt-2 mt-md-0">
        <div class="button-container">
          <b-button
            v-if="!importButtonDisabled"
            class="w-100 text-nowrap"
            @click="openFileInput"
            :disabled="disabled || uploading">
            Importer depuis mon ordinateur
          </b-button>
          <b-button
            v-if="!chooseButtonDisabled"
            class="w-100 mt-2 text-nowrap"
            @click="openLibraryModal"
            :disabled="disabled || uploading">
            Choisir dans la bibliothèque
          </b-button>
          <b-button
            v-if="hasSelectedAudio && !resetButtonDisabled"
            class="w-100 mt-2 text-nowrap"
            variant="danger"
            @click="resetAudio"
            :disabled="disabled || uploading">
            Réinitialiser
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Input pour l'import de fichier -->
    <input type="file" ref="fileInput" style="display: none" @change="onFileSelected" accept="audio/*" />

    <!-- Modal pour la bibliothèque d'audios -->
    <b-modal ref="libraryModal" title="Choisir un audio" size="xl" centered hide-footer>
      <b-row class="mb-4">
        <b-col cols="12">
          <b-form-group id="fieldset-search-uploads-1" label="Rechercher" label-for="search-uploads">
            <b-form-input id="search-uploads" v-model="search" trim></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-container>
          <!-- loading -->
          <div v-if="isLoading">
            <b-spinner />
          </div>

          <!-- empty container -->
          <div v-if="list.items.length === 0 && !isLoading">
            <p class="text-center">Aucun fichier trouvé</p>
          </div>

          <!-- table content -->
          <b-table-simple striped small hover v-else-if="list.items.length > 0">
            <b-thead>
              <b-tr>
                <b-th style="width: 1px">&nbsp;</b-th>
                <b-th>Nom</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr
                v-for="(file, index) in list.items"
                :key="index"
                :variant="itemsVariantsById[file.id] || null"
                class="cursor-pointer"
                @click="selectLibraryFile(file)"
                v-b-tooltip.hover.bottom
                :title="file.name">
                <b-td>
                  <b-button v-if="!isPlaying(file)" @click.stop="playAudio(file)" variant="primary" size="sm">
                    <b-icon-play-fill />
                  </b-button>
                  <b-button v-else @click.stop="stopAudio()" variant="danger" size="sm">
                    <b-icon-stop-fill />
                  </b-button>
                </b-td>
                <b-td>{{ file.name }}</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>

          <!-- pagination -->
          <div v-if="list.meta.total > 0" class="pagination-bar mb-3">
            <div class="pagination-total">{{ list.meta.total }} élément(s)</div>
            <div class="pagination-center">
              <b-pagination
                v-model="currentPage"
                :total-rows="list.meta.total"
                :per-page="perPage"
                align="center"
                @page-click="(event, page) => fetchFilesLibrary(page)"
                class="m-0"></b-pagination>
            </div>
            <div class="pagination-select">
              <b-form-select
                v-model="perPage"
                :options="[
                  { value: 10, text: '10 par page' },
                  { value: 25, text: '25 par page' },
                  { value: 50, text: '50 par page' },
                  { value: 100, text: '100 par page' },
                ]"
                size="sm"
                style="width: 120px"
                @change="handlePerPageChange"></b-form-select>
            </div>
          </div>
        </b-container>
      </b-row>
    </b-modal>

    <audio ref="audioPlayer" class="d-none"></audio>
  </div>
</template>

<script>
  import axios from 'axios'
  import _isEqual from 'lodash/isEqual'
  import _debounce from 'lodash/debounce'
  import { BIconPlayFill, BIconStopFill } from 'bootstrap-vue'
  import fileService from '@/services/fileService'

  export default {
    name: 'AudioSelector',
    components: {
      BIconPlayFill,
      BIconStopFill,
    },
    props: {
      buttonsPosition: {
        type: String,
        default: 'bottom',
        validator: (value) => ['bottom', 'right'].includes(value),
      },
      initialAudio: {
        type: Object,
        default: null,
      },
      emptyContainerAction: {
        type: String,
        default: 'none',
        validator: (value) => ['none', 'import', 'choose'].includes(value),
      },
      uploadFileCategory: {
        type: String,
        default: 'default',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      importButtonDisabled: {
        type: Boolean,
        default: false,
      },
      chooseButtonDisabled: {
        type: Boolean,
        default: false,
      },
      resetButtonDisabled: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        selectedAudio: this.initialAudio,
        showOverlay: false,
        uploading: false,
        search: '',
        playingAudio: null,
        debouncedSearch: null,

        isLoading: false,
        currentPage: 1,
        perPage: 10,
        list: {
          items: [],
          meta: {
            total: 0,
            page: this.currentPage,
            perPage: this.perPage,
            totalPages: 0,
            prevPage: null,
            nextPage: null,
          },
        },
      }
    },
    created() {
      this.debouncedSearch = _debounce(this.handleSearch, 500)
    },
    watch: {
      search(newValue) {
        this.debouncedSearch(newValue)
      },
      initialAudio(newValue, oldValue) {
        if (_isEqual(newValue, oldValue)) {
          return
        }

        this.selectedAudio = newValue
      },
    },
    computed: {
      jwt() {
        return localStorage.getItem('jwt')
      },
      hasSelectedAudio() {
        return !!this.selectedAudio && !!this.selectedAudio.name
      },
      itemsVariantsById() {
        return this.list.items.reduce((acc, item) => {
          let rowVariant = null
          if (item.status === 'CREATED') {
            rowVariant = 'success'
          } else if (item.status === 'UPDATED') {
            rowVariant = 'warning'
          } else if (item.status === 'DELETED') {
            rowVariant = 'danger'
          }

          acc[item.id] = rowVariant
          return acc
        })
      },
    },
    methods: {
      async handleSearch() {
        await this.fetchFilesLibrary(1)
      },
      async handlePerPageChange() {
        this.currentPage = 1
        await this.fetchFilesLibrary(1)
      },
      async fetchFilesLibrary(newPage) {
        this.list = await fileService.paginate({
          page: newPage,
          limit: this.perPage,
          type: 'audio',
          search: this.search,
        })
      },
      isPlaying(audio) {
        return this.playingAudio && this.playingAudio.name === audio.name && this.playingAudio.path === audio.path
      },
      playAudio(audio) {
        // stop audio if already playing
        if (this.playingAudio) {
          this.$refs.audioPlayer.pause()
        }

        this.$refs.audioPlayer.src = audio.path
        this.$refs.audioPlayer.play()
        this.playingAudio = audio
      },
      stopAudio() {
        this.$refs.audioPlayer.pause()
        this.playingAudio = null
      },

      handleEmptyContainerClick() {
        if (this.uploading) {
          return
        }

        if (this.disabled) {
          return
        }

        if (!this.hasSelectedAudio) {
          if (this.emptyContainerAction === 'import') {
            this.openFileInput()
          } else if (this.emptyContainerAction === 'choose') {
            this.openLibraryModal()
          }
        }
      },
      openFileInput() {
        this.$refs.fileInput.click()
      },
      async openLibraryModal() {
        this.isLoading = true
        await this.fetchFilesLibrary(this.currentPage)
        this.isLoading = false
        this.$refs.libraryModal.show()
      },
      async onFileSelected(event) {
        const file = event.target.files[0]
        if (!file) {
          return
        }

        this.uploading = true
        try {
          const uploadedAudioResponse = await this.uploadFile(file)
          const uploadedAudio = uploadedAudioResponse
          const uploadedAudioData = {
            name: uploadedAudio.name,
            path: uploadedAudio.path,
            url: uploadedAudio.url,
          }
          this.selectedAudio = uploadedAudioData
          this.$emit('file-uploaded', uploadedAudioData)
          this.$emit('file-selected', uploadedAudioData)
          this.$notify({
            type: 'success',
            title: 'Fichier téléchargé',
            text: 'Le fichier a été téléchargé avec succès',
          })
        } catch (e) {
          this.$notify({
            type: 'error',
            title: e || 'Erreur lors du téléchargement',
            text: e || 'Une erreur est survenue lors du téléchargement du fichier',
          })
        } finally {
          this.uploading = false
        }
      },
      async uploadFile(file) {
        if (!file) {
          return
        }

        // 🟠 L'ordre des paramètres est important à cause de multer !
        // See: https://medium.com/@muh__hizbullah/multer-req-body-null-object-when-send-file-with-other-field-string-using-formdata-b275b4364404
        const formData = new FormData()
        formData.append('category', this.uploadFileCategory)
        formData.append('file', file)

        const response = await axios.post('/api/files', formData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: 'Bearer ' + this.jwt,
          },
        })

        return response.data
      },
      selectLibraryFile(audio) {
        this.selectedAudio = audio
        this.$emit('file-selected', this.selectedAudio)
        this.$refs.libraryModal.hide()
      },
      resetAudio() {
        this.selectedAudio = null
        this.$emit('file-selected', null)
      },
    },
  }
</script>

<style>
  .audio-container.empty {
    background-color: #f8f9fa;
  }

  .placeholder {
    color: #6c757d;
    border: 2px dashed #6c757d;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100px;
  }
</style>

<style scoped>
  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: nowrap;
  }
  .pagination-total {
    flex: 1;
    text-align: left;
    min-width: 120px;
  }
  .pagination-center {
    flex: 2;
    display: flex;
    justify-content: center;
    min-width: 200px;
  }
  .pagination-select {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    min-width: 160px;
  }
  @media (max-width: 768px) {
    .pagination-bar {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    .pagination-total,
    .pagination-center,
    .pagination-select {
      text-align: center;
      justify-content: center !important;
    }
  }
</style>
